<template>
  <BookingComponent
    :booking="booking"
    :job="job"
    :existing-booking="existingBooking"
    :is-new-job="isNewJob"
    :is-admin-type="isAdminType"
    :is-read-only="isReadOnly"
    :is-admin="isAdmin()"
    :is-new-booking="isNewBooking"
    :has-repeating="hasRepeating"
    :selected-client="selectedClient"
    :client-contacts="selectedClientContacts"
    :producers="producers"
    :all-resources="allResources"
    :repeating-dates="repeatingDates"
    :get-bookings-for-resources="getBookingsForResources"
    :existing-resource-ids="existingResourceIds"
    :activities="activities"
    :dates="dates"
    :max-title-length="maxTitleLength"
    :linked-job-title="linkedJobTitle"
    :booking-status="bookingStatus"
    :job-status="jobStatus"
    :find-job="findJob"
    :find-clients="findClients"
    :label="label"
    v-on:toggle-new-job="toggleNewJob"
    v-on:clear-booking-job="clearBookingJob"
    v-on:select-job="selectJob"
    v-on:link-job="linkJob"
    v-on:select-client="selectClient"
    v-on:select-resource="selectResource"
    v-on:update-repeat="updateRepeat"
    v-on:save="save"
    v-on:delete="onDelete"
    v-on:cancel="cancel"
    v-on:open-job-window="openJobWindow"
  />
</template>

<script>
import { loadUserAttributes } from '../../common/service/userAttributes.js'
import { start as startBookingTiming } from '../service/bookingTiming.js'
import moment from 'moment-timezone'
import * as _ from 'lodash-es'

import alertService from '../../common/service/alertService.js'
import { contactLabels } from '../../common/service/clientContacts.js'
import bookingService from '../service/bookingService.js'
import searchService from '../../search/service/searchService.js'
import clientRefreshService from '../../clientRefresh/service/ClientRefreshService.js'
import bookingStatus from '../../common/bookingStatus.js'
import resourcesService from '../../resources/service/resourcesService.js'
import jobService from '../../job/service/jobService.js'
import jobStatus from '../../common/jobStatus.js'
import loginService from '../../login/service/loginService.js'
import roleService from '../../login/service/roleService.js'
import sequenceService from '../../common/service/sequenceService.js'
import BookingComponent from './bookingComponent.vue'

// The booking slide-out host (step 15b of #614). This is `BookingCtrl` as a Vue component: it
// prepares the draft booking and owns the state `BookingComponent` is handed as props. The
// component reads and mutates the same `booking`/`job` objects, as it did through ngVue.
//
// `close` resolves the slide-out promise with the value the controller passed to
// `$uibModalInstance.close(1)`; there is no `dismiss` path - BookingCtrl's cancel closes rather
// than dismisses.
export default {
  name: 'BookingSlider',
  components: { BookingComponent },
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      displayDateFormat: 'DD-MM-YYYY HH:mm',
      maxTitleLength: 50,
      dates: ['Daily', 'Weekly', 'Monthly', 'Yearly'],
      repeatingDates: [],
      producers: [],
      selectedClientContacts: [],
      selectedClient: undefined,
      bookingStatus,
      jobStatus: jobStatus,
      allResources: resourcesService.getAll(),
      isAdminType: false,
      isNewJob: false,
      isNewBooking: false,
      hasRepeating: false,
      isReadOnly: false,
      linkedJobTitle: undefined,
      existingBooking: undefined,
      existingResourceIds: [],
      activities: [],
      job: undefined,
      stopWatching: [],
    }
  },
  created() {
    this.prepareBooking(this.booking)

    this.existingBooking = _.cloneDeep(this.booking)
    this.existingResourceIds = this.booking.bookingEvents.map(
      bookingEvent => (bookingEvent.resource && bookingEvent.resource._id) || bookingEvent.resource
    )
    this.booking.existingRepeat = this.existingBooking.repeatEvery
    this.isNewBooking = this.existingBooking.activity === undefined
    this.activities = this.getActivities()
    // Angular's $watch fired on the first digest, so the controller's isReadOnly was set for the
    // status the booking arrived with; Vue's $watch does not, so do it here as well.
    this.isReadOnly = this.computeReadOnly()

    this.stopWatching = [
      this.$watch('isAdminType', () => {
        this.activities = this.getActivities()
      }),
      // The status drives isReadOnly, and a status other than confirmed clears the critical task.
      this.$watch('booking.status', () => {
        if (this.booking.status !== bookingStatus.CONFIRMED) {
          this.booking.criticalTask = undefined
        }
        this.isReadOnly = this.computeReadOnly()
      }),
    ]
  },
  beforeUnmount() {
    this.stopWatching.forEach(stop => stop())
  },
  methods: {
    isAdmin() {
      return roleService.hasRole('admin')
    },
    // Was the body of the controller's `$watch('booking.status')`.
    computeReadOnly() {
      return (
        this.booking.status === bookingStatus.EXTERNAL ||
        (!roleService.hasRole('admin') &&
          (this.booking.status === bookingStatus.COMPLETED || this.booking.status === bookingStatus.UNFUCKABLE))
      )
    },
    getBookingsForResources() {
      // A function, not the object: the service replaces the cache on every refresh, so the
      // component reads the current one each call.
      return bookingService.getBookingsForResources()
    },
    getActivities() {
      return this.isAdminType ? bookingService.gatherAdminBookingActivities() : bookingService.gatherActivities()
    },
    async prepareBooking(booking) {
      var startsAt = moment(booking.startsAt)
      var endsAt = moment(booking.endsAt)
      var endsOn = moment(booking.endsOn)
      booking.startsAt = startsAt.format(this.displayDateFormat)
      booking.endsAt = endsAt.format(this.displayDateFormat)
      if (!booking.endsOn) {
        booking.endsOn = endsAt.format(this.displayDateFormat)
      } else {
        booking.endsOn = endsOn.format(this.displayDateFormat)
      }

      booking.repeating = null
      booking.hours = endsAt.diff(startsAt, 'hours', true)
      this.hasRepeating = Boolean(booking.repeatingId)

      if (booking.linkedJobId) {
        const linkedJob = await jobService.findById(booking.linkedJobId)
        this.linkedJobTitle = this.jobPrefix + linkedJob.jobNumber + ' - ' + linkedJob.title
      }

      this.isAdminType = booking._id ? booking.jobNumber === undefined : false
      this.job = !this.isAdminType ? jobService.getCurrentJob() : undefined

      if (!this.isAdminType && !booking.status) {
        if (roleService.hasRole('admin')) {
          if (this.job && this.job.status !== jobStatus.QUOTING) {
            booking.status = bookingStatus.CONFIRMED
          } else {
            booking.status = bookingStatus.QUOTING
          }
        } else {
          booking.status = bookingStatus.CONFIRMED
        }
      }
    },

    async toggleNewJob() {
      if (!this.producers || this.producers.length === 0) {
        this.producers = await jobService.getProducers()
      }

      const turningOn = this.isNewJob === false

      if (this.isAdminType) {
        this.isNewJob = false
        this.job = undefined
      } else if (turningOn) {
        // await the new job BEFORE flipping isNewJob: otherwise the form renders for a tick with
        // isNewJob true and job null, and `v-model="job.title"` throws
        this.job = await jobService.addJob({ openWindow: false })
        this.isNewJob = true
      } else {
        this.isNewJob = false
        this.job = undefined
        this.booking.jobNumber = undefined
      }

      if (this.isNewJob) {
        this.booking.status = this.isAdmin() ? this.jobStatus.QUOTING : this.bookingStatus.CONFIRMED
      } else if (this.isAdminType) {
        this.booking.status = undefined
      } else {
        this.booking.status = this.isAdmin() ? this.jobStatus.QUOTING : this.bookingStatus.CONFIRMED
      }

      return this.job
    },
    clearBookingJob() {
      this.booking.jobNumber = undefined
      this.isAdminType = !this.isAdminType
    },
    selectJob(job) {
      this.booking.jobNumber = job.jobNumber
      this.job = job
    },
    linkJob(job) {
      this.booking.linkedJobId = job._id
      this.job = job
    },
    selectClient(client) {
      this.selectedClient = client
      // Deduplicated by name, and disambiguated by email only where two contacts share one (#300).
      this.selectedClientContacts = contactLabels(client.contacts)
      if (this.job) {
        this.job.client = client.accountName
      }
    },
    findJob(query) {
      return searchService.findJobWithText(query, true)
    },
    findClients(clientName) {
      return clientRefreshService.findClients(clientName).then(clients => (clients.data ? clients.data.clients : []))
    },
    label(item) {
      if (!item || !item.jobNumber) {
        return
      }
      var label = this.jobPrefix + item.jobNumber
      if (item.title) {
        label += ' - ' + item.title
      }
      return label
    },
    selectResource(resource) {
      if (resource) {
        sequenceService.next('bookingEvent').then(httpResult => {
          this.booking.bookingEvents.push({
            sequence: httpResult.data.nextItem,
            resource: resource,
            parentBookingSequence: this.booking.sequence,
          })
        })
      }
    },
    updateRepeat(booking) {
      if (!booking.repeatEvery) {
        this.repeatingDates = []
        return
      }
      bookingService.getRepeatBooking(booking).then(res => {
        this.repeatingDates = res || []
      })
    },
    save({ mode }) {
      if (mode === 'update-repeat') {
        this.booking.updateRepeating = true
      }
      if (mode === 'save') {
        this.booking.repeating = null
      } else if (mode === 'add-repeat' || mode === 'update-repeat') {
        this.booking.repeating = { displayDateFormat: this.displayDateFormat }
      }

      this.booking.startsAt = moment(this.booking.startsAt, this.displayDateFormat).toDate()
      this.booking.endsAt = moment(this.booking.endsAt, this.displayDateFormat).toDate()
      this.booking.sequence = _.toString(this.booking.sequence)

      this.saveBooking()
    },
    async saveBooking() {
      // The metric the owner asked for: the whole click-to-confirmed path, staged (#671 sidebar).
      const timing = startBookingTiming(this.booking && this.booking._id ? 'update' : 'add', this.calendarView)

      for (const bookingEvent of this.booking.bookingEvents) {
        this.booking.wasAdmin = bookingEvent.isAdminType
        bookingEvent.isAdminType = this.isAdminType
      }

      this.booking.changedJobNumber =
        this.existingBooking.jobNumber !== this.booking.jobNumber ? this.existingBooking.jobNumber : undefined
      this.booking.existingNotes = this.existingBooking.notes

      timing.mark('prepare')
      const user = await loginService.currentUser()
      const userAttributes = await loadUserAttributes()
      timing.mark('user')

      if (!this.booking.createdBy) {
        this.booking.createdAt = new Date()
        this.booking.createdBy = userAttributes.name || user.username
      }

      if (this.job && !this.job.jobNumber) {
        this.job.createdOn = moment()
      }

      if (this.job && !this.job.status) {
        this.job.status = this.isAdmin() ? this.jobStatus.QUOTING : this.jobStatus.IN_PROGRESS
      }

      try {
        await bookingService.saveBooking(this.booking, undefined, this.job)
        timing.mark('save')
      } catch (err) {
        // A rejected save used to disappear: the panel closed, the calendar re-rendered from the
        // server unchanged, and the booking looked as if it had simply vanished. It stayed put - the
        // server had refused it (#456). Say so, and leave the panel open so it can be fixed.
        const message =
          (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || ''
        alertService.add('error', message || 'The booking could not be saved.', 'Booking not saved')
        await this.refreshBookings()
        timing.finish(false)
        return
      }

      timing.finish(true)
      this.$emit('close', 1)
    },
    async refreshBookings() {
      // The calendar is rendered from the store, so a refused save needs it refetched to show what
      // the server actually has.
      try {
        await bookingService.refreshBookings('day')
      } catch (err) {
        console.log('Could not refresh bookings after a failed save', err)
      }
    },
    onDelete({ type }) {
      if (type === 'repeat') {
        bookingService.deleteRepeatBooking(this.booking)
      } else {
        bookingService.deleteBooking(this.booking)
      }
      this.$emit('close', 1)
    },
    cancel() {
      this.$emit('close', 1)
    },
    openJobWindow() {
      jobService.setCurrentJob(this.job)
      this.$emit('close', 1)
      jobService.openJobWindow()
    },
  },
}
</script>
